import { ChevronDown, Plus, RefreshCw, Search, X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { channelLabel, modeLabel, statusLabel } from '../conversations/format';

import type { InboxFilters, InboxSortOption } from './types';

export interface InboxToolbarProps {
  filters: InboxFilters;
  activeFilters: string[];
  sortOption: InboxSortOption;
  onSortChange: (option: InboxSortOption) => void;
  onSearchChange: (search: string) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
}

/**
 * Toolbar da Inbox (Brief 2B T1).
 *
 * Contém:
 *   - Campo de busca (nome/telefone/ID) — kbd "/"
 *   - Dropdown de ordenação com prefixo "Ordenar:"
 *   - Chips de filtros ativos (X decorativo) + botão Limpar (limpa tudo)
 *   - Botão Atualizar (RefreshCw)
 *   - CTA "Iniciar Conversa" → navega para /iniciar-conversa (Tela 8 greenfield);
 *     atalho ⌘N / Ctrl+N também navega.
 */
export function InboxToolbar({
  filters,
  activeFilters,
  sortOption,
  onSortChange,
  onSearchChange,
  onClearFilters,
  onRefresh,
}: InboxToolbarProps): JSX.Element {
  const navigate = useNavigate();

  // Atalho ⌘N / Ctrl+N → Iniciar Conversa (mesmo destino do CTA).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        navigate('/iniciar-conversa');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  const sortOptions: { value: InboxSortOption; label: string }[] = [
    { value: 'urgency', label: 'Urgência (Conflict + SLA)' },
    { value: 'recency', label: 'Mais recentes' },
    { value: 'aging', label: 'Mais antigas' },
  ];

  return (
    <div className="flex items-center gap-2 px-6 py-2 border-b border-border-subtle bg-surface shrink-0 flex-wrap">
      {/* Search */}
      <div className="input-wrap flex-1 min-w-48 max-w-80">
        <Search size={13} aria-hidden />
        <input
          type="search"
          placeholder="Buscar por nome, telefone ou ID…"
          value={filters.search ?? ''}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label="Buscar conversas"
        />
        <span className="kbd ml-auto" aria-hidden>
          /
        </span>
      </div>

      {/* Sort Dropdown */}
      <SortDropdown value={sortOption} onChange={onSortChange} options={sortOptions} />

      {/* Active filter chips (X decorativo — design não tem handler individual) */}
      {activeFilters.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {activeFilters.map((f) => (
            <span key={f} className="filter-chip filter-chip--active">
              {f}
              <X size={9} className="filter-chip__close" aria-hidden />
            </span>
          ))}
          <button
            type="button"
            onClick={onClearFilters}
            className="btn btn--ghost btn--xs text-text-secondary"
          >
            Limpar
          </button>
        </div>
      )}

      <div className="flex-1" />

      {/* Refresh */}
      <button
        type="button"
        onClick={onRefresh}
        className="btn btn--ghost btn--sm"
        title="Atualizar (Ctrl+R)"
      >
        <RefreshCw size={13} aria-hidden />
        Atualizar
      </button>

      {/* Iniciar Conversa — navega para a Tela 8 (greenfield) */}
      <button
        type="button"
        onClick={() => navigate('/iniciar-conversa')}
        className="btn btn--primary btn--sm"
        title="Nova conversa (⌘N)"
      >
        <Plus size={13} aria-hidden />
        Iniciar Conversa
        <span className="kbd ml-1" aria-hidden>
          ⌘N
        </span>
      </button>
    </div>
  );
}

// ─── Internal SortDropdown — mantém <select> por trás, visual com rótulo "Ordenar:" ───

function SortDropdown({
  value,
  onChange,
  options,
}: {
  value: InboxSortOption;
  onChange: (v: InboxSortOption) => void;
  options: { value: InboxSortOption; label: string }[];
}): JSX.Element {
  return (
    <div className="relative inline-flex items-center">
      <span className="absolute left-2 text-text-secondary text-xs pointer-events-none z-10">
        Ordenar:
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as InboxSortOption)}
        className="input input--sm pl-16 pr-7 appearance-none cursor-pointer"
        aria-label="Ordenar por"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={12}
        className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary"
        aria-hidden
      />
    </div>
  );
}

/**
 * Serializa filtros ativos para labels legíveis (usadas nos chips).
 */
export function buildActiveFilterLabels(filters: InboxFilters): string[] {
  const labels: string[] = [];
  if (filters.status) labels.push(`Status: ${statusLabel(filters.status)}`);
  if (filters.channel) labels.push(`Canal: ${channelLabel(filters.channel)}`);
  if (filters.operationMode) labels.push(`Modo: ${modeLabel(filters.operationMode)}`);
  if (filters.assignedToUserId === 'me') labels.push('Minhas');
  if (filters.assignedToUserId === 'unassigned') labels.push('Não atribuídas');
  if (filters.awaitingParty) labels.push(`Aguardando: ${AWAITING_LABELS[filters.awaitingParty]}`);
  // intentFilter: honest-defer (Item NEW-C2) — param sem efeito no backend; sem chip.
  if (filters.commitmentFilter === 'broken') labels.push('Compromisso vencido');
  if (filters.sequenceFlag) labels.push('Sequência ativa');
  if (filters.metaSessionType)
    labels.push(
      `Sessão Meta: ${META_FILTER_LABELS[filters.metaSessionType] ?? filters.metaSessionType}`,
    );
  if (filters.inGroup) labels.push('Em grupo');
  if (filters.search) labels.push(`Busca: "${filters.search}"`);
  return labels;
}

const AWAITING_LABELS: Record<'customer' | 'operator' | 'system', string> = {
  customer: 'Cliente',
  operator: 'Operador',
  system: 'Sistema',
};

const META_FILTER_LABELS: Record<string, string> = {
  customer_care: 'Janela 24h',
  service: 'Atendimento',
  utility: 'Utilitário',
  authentication: 'Autenticação',
  marketing: 'Marketing',
  ctwa: 'Anúncio (CTWA)',
};
